import { Controller } from "@hotwired/stimulus"

/**
 * Assistant Notifier Controller
 *
 * Lives on <body> and lets a multitasking user know when the assistant has
 * finished answering while they were looking elsewhere — another tab, another
 * browser window, or another app.
 *
 * Completion is detected by watching the Turbo Stream that swaps the send
 * button back from its processing state to its ready state (the button element
 * is replaced wholesale, so we cannot rely on a Stimulus value transition).
 * When the button goes busy -> ready AND the user is not looking at this tab, we:
 *   - play a short synthesized chime (Web Audio), if the user opted in,
 *   - prefix the tab title with a dot, and
 *   - paint a Gobierto-pink badge on the favicon.
 *
 * "Not looking" means the tab is hidden OR the window does not have OS focus,
 * so switching to another window/app counts too — not just other tabs.
 *
 * The title/favicon indicators are always on (silent, only shown while away);
 * the chime is opt-in via the `soundEnabled` value, kept in sync with the nav
 * toggle through the `assistant-notifier:sound-changed` window event. Switching
 * the toggle on plays a one-off preview chime regardless of focus.
 *
 * Known limitation: detection relies on the completion Turbo Stream arriving on
 * the page that started the answer. If the user Turbo-visits a different Gobierto
 * page in the *same tab* while a reply is in flight, that page no longer
 * subscribes to the conversation stream, so the completion is not detected there.
 * Cross-navigation persistence (e.g. an in-flight flag in sessionStorage backed
 * by ActionCable) is intentionally out of scope for now.
 */
const TITLE_PREFIX = "● "
const PROCESSING_ATTRIBUTE = "data-send-button-processing-value"
const SEND_BUTTON_TARGET = "send_button_container"
const BADGE_COLOR = "#FF776D" // --color-gobpink

export default class extends Controller {
  static values = {
    soundEnabled: { type: Boolean, default: false }
  }

  connect() {
    this.processing = this._sendButtonProcessing()
    this.faviconBadged = false

    this._onStreamRender = this._handleStreamRender.bind(this)
    this._onUserBack = this._restoreIndicators.bind(this)
    this._onSoundChanged = (event) => {
      this.soundEnabledValue = event.detail.enabled
      // Preview the chime when the user switches it on, so they hear what it is.
      // Deliberate action while looking at the UI, so it plays regardless of focus.
      if (event.detail.enabled) this._playChime()
    }

    document.addEventListener("turbo:before-stream-render", this._onStreamRender)
    document.addEventListener("visibilitychange", this._onUserBack)
    window.addEventListener("focus", this._onUserBack)
    window.addEventListener("assistant-notifier:sound-changed", this._onSoundChanged)
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this._onStreamRender)
    document.removeEventListener("visibilitychange", this._onUserBack)
    window.removeEventListener("focus", this._onUserBack)
    window.removeEventListener("assistant-notifier:sound-changed", this._onSoundChanged)
  }

  // --- Completion detection ---

  _handleStreamRender(event) {
    const stream = event.target
    if (!stream || stream.getAttribute("target") !== SEND_BUTTON_TARGET) return

    const processing = this._streamButtonProcessing(stream)
    if (processing === null) return

    if (processing) {
      this.processing = true
    } else if (this.processing || this._sendButtonProcessing()) {
      // Belt-and-suspenders: also treat "ready" as a completion when the live
      // button is still busy, in case the earlier busy stream was missed. The
      // DOM hasn't been swapped yet (this fires on before-stream-render), so
      // _sendButtonProcessing() still reflects the pre-swap state.
      this.processing = false
      this._notifyCompleted()
    }
  }

  _notifyCompleted() {
    // Only notify when the user is looking elsewhere — when they are actively on
    // this tab they can already watch the answer stream in.
    if (this._userIsLooking()) return

    if (this.soundEnabledValue) this._playChime()
    this._markTitle()
    this._markFavicon()
  }

  _restoreIndicators() {
    if (!this._userIsLooking()) return

    this._restoreTitle()
    this._restoreFavicon()
  }

  // The user is paying attention only when this tab is both visible and the
  // window/app has OS focus. Anything else (other tab, other window, other app)
  // counts as "away".
  _userIsLooking() {
    return !document.hidden && document.hasFocus()
  }

  // --- Sound (synthesized, no asset) ---

  _playChime() {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext
      if (!AudioCtx) return

      if (!this.audioContext) this.audioContext = new AudioCtx()
      const context = this.audioContext
      if (context.state === "suspended") context.resume()

      const now = context.currentTime
      this._playTone(context, 880.0, now, 0.12)
      this._playTone(context, 1318.5, now + 0.1, 0.2)
    } catch (error) {
      // Audio is a nice-to-have; never let it break the page.
    }
  }

  _playTone(context, frequency, startTime, duration) {
    const oscillator = context.createOscillator()
    const gain = context.createGain()

    oscillator.type = "sine"
    oscillator.frequency.value = frequency

    gain.gain.setValueAtTime(0.0001, startTime)
    gain.gain.linearRampToValueAtTime(0.15, startTime + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration)

    oscillator.connect(gain).connect(context.destination)
    oscillator.start(startTime)
    oscillator.stop(startTime + duration)
  }

  // --- Tab title indicator ---

  _markTitle() {
    if (document.title.startsWith(TITLE_PREFIX)) return

    document.title = TITLE_PREFIX + document.title
  }

  _restoreTitle() {
    if (!document.title.startsWith(TITLE_PREFIX)) return

    document.title = document.title.slice(TITLE_PREFIX.length)
  }

  // --- Favicon badge (Gobierto pink) ---

  _markFavicon() {
    const links = this._iconLinks()
    if (!links.length || this.faviconBadged) return

    this.faviconBadged = true
    if (!this.originalFavicons) {
      this.originalFavicons = links.map((link) => ({ link, href: link.getAttribute("href") }))
    }

    const source = this.originalFavicons[this.originalFavicons.length - 1]?.href
    if (!source) return

    const image = new Image()
    image.onload = () => {
      // The user may have come back before the image finished loading.
      if (!this.faviconBadged) return

      try {
        const size = image.width || 32
        const canvas = document.createElement("canvas")
        canvas.width = size
        canvas.height = size

        const ctx = canvas.getContext("2d")
        if (!ctx) return

        ctx.drawImage(image, 0, 0, size, size)

        const radius = Math.max(4, size * 0.3)
        const center = size - radius
        ctx.beginPath()
        ctx.arc(center, center, radius, 0, 2 * Math.PI)
        ctx.fillStyle = BADGE_COLOR
        ctx.fill()
        ctx.lineWidth = Math.max(1, size * 0.06)
        ctx.strokeStyle = "#ffffff"
        ctx.stroke()

        const badged = canvas.toDataURL("image/png")
        this.originalFavicons.forEach(({ link }) => link.setAttribute("href", badged))
      } catch (error) {
        // Drawing can fail (tainted canvas, no 2d context); skip the badge.
      }
    }
    image.src = source
  }

  _restoreFavicon() {
    this.faviconBadged = false
    if (!this.originalFavicons) return

    this.originalFavicons.forEach(({ link, href }) => {
      if (href) link.setAttribute("href", href)
    })
  }

  // --- Helpers ---

  _iconLinks() {
    return Array.from(document.querySelectorAll("link[rel~='icon']"))
  }

  _streamButtonProcessing(stream) {
    const template = stream.querySelector("template")
    const button = template?.content?.querySelector(`[${PROCESSING_ATTRIBUTE}]`)
    if (!button) return null

    return button.getAttribute(PROCESSING_ATTRIBUTE) === "true"
  }

  _sendButtonProcessing() {
    const button = document.querySelector(`#send_button[${PROCESSING_ATTRIBUTE}]`)
    return button?.getAttribute(PROCESSING_ATTRIBUTE) === "true"
  }
}
